import React, { Fragment } from 'react'
import { Card, CardContent, Typography, Box, Divider } from '@mui/material'
import { themeColors } from '../../app/layout/theme'

export interface SubStatus {
  label: string
  value: number | string
  color?: string
}

interface PipelineSummaryCardProps {
  title: string
  total: number | string
  icon?: React.ReactNode
  color?: string
  bgcolor?: string
  subStatuses: SubStatus[]
  onClick?: () => void
}

export const PipelineSummaryCard = ({
  title,
  total,
  icon,
  color = themeColors.primary,
  bgcolor = themeColors.surface,
  subStatuses,
  onClick,
}: PipelineSummaryCardProps) => {
  return (
    <Card
      onClick={onClick}
      sx={{
        background: bgcolor,
        borderRadius: '10px',
        border: `1px solid ${themeColors.border}`,
        boxShadow: themeColors.shadow.sm,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.15s ease',
        '&:hover': onClick
          ? {
              transform: 'translateY(-0.5px)',
              boxShadow: themeColors.shadow.md,
              borderColor: themeColors.surfaceHover,
            }
          : {},
      }}
    >
      <CardContent sx={{ p: '16px !important' }}>
        {/* Top Row: Title and Total */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {icon && (
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '8px',
                  backgroundColor: `${color}12`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '& svg': { fontSize: 18, color: color },
                }}
              >
                {icon}
              </Box>
            )}
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: themeColors.text.primary }}>
              {title}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
            <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary, fontWeight: 500 }}>
              Total:
            </Typography>
            <Typography sx={{ fontSize: '20px', fontWeight: 700, color: themeColors.text.primary, lineHeight: 1 }}>
              {total}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 1.5, borderColor: themeColors.borderLight }} />

        {/* Sub Statuses: Justify Between with Vertical Dividers */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          {subStatuses.map((status, idx) => (
            <Fragment key={idx}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography sx={{ fontSize: '13px', color: themeColors.text.secondary }}>
                  {status.label}:
                </Typography>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: themeColors.text.primary }}>
                  {status.value}
                </Typography>
              </Box>
              {idx < subStatuses.length - 1 && (
                <Divider orientation="vertical" flexItem sx={{ my: 0.5, borderColor: themeColors.borderLight }} />
              )}
            </Fragment>
          ))}
        </Box>
      </CardContent>
    </Card>
  )
}

